import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  BedDouble, CalendarDays, RefreshCw, Eye, LogIn, LogOut, Banknote,
  User, Phone, Mail, Tag, Percent, DollarSign,
} from 'lucide-react';
import {
  getAdminBookings, getRoomCalendar, getAdminBookingDetail,
  checkIn, checkOut, confirmPayment,
} from '../services/adminService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// ── constants ─────────────────────────────────────────────────────────────────

const ROOM_STYLE = {
  Free:    { card: 'bg-green-500/10 border-green-500/30 hover:border-green-400 cursor-pointer', dot: 'bg-green-400', label: 'text-green-400' },
  Booked:  { card: 'bg-blue-500/10  border-blue-500/30',  dot: 'bg-blue-400',  label: 'text-blue-400'  },
  Blocked: { card: 'bg-red-500/10   border-red-500/30',   dot: 'bg-red-400',   label: 'text-red-400'   },
};

const STATS = [
  { key: 'available',  label: 'Available',   bg: 'bg-green-500/15',  text: 'text-green-400'  },
  { key: 'total',      label: 'Total',        bg: 'bg-slate-700/60',  text: 'text-white'       },
  { key: 'Hold',       label: 'Hold',         bg: 'bg-yellow-500/15', text: 'text-yellow-400'  },
  { key: 'Confirmed',  label: 'Confirmed',    bg: 'bg-blue-500/15',   text: 'text-blue-400'    },
  { key: 'CheckedIn',  label: 'Checked In',   bg: 'bg-teal-500/15',   text: 'text-teal-400'    },
  { key: 'CheckedOut', label: 'Checked Out',  bg: 'bg-slate-500/15',  text: 'text-slate-400'   },
  { key: 'Cancelled',  label: 'Cancelled',    bg: 'bg-red-500/15',    text: 'text-red-400'     },
  { key: 'NoShow',     label: 'No Show',      bg: 'bg-orange-500/15', text: 'text-orange-400'  },
];

const STATUS_STYLE = {
  Hold:      'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Confirmed: 'bg-blue-500/20   text-blue-400   border-blue-500/30',
  CheckedIn: 'bg-green-500/20  text-green-400  border-green-500/30',
  CheckedOut:'bg-slate-500/20  text-slate-400  border-slate-500/30',
  Cancelled: 'bg-red-500/20    text-red-400    border-red-500/30',
  NoShow:    'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const PAY_STYLE = {
  Pending: 'bg-yellow-500/20 text-yellow-400',
  Partial: 'bg-blue-500/20   text-blue-400',
  Paid:    'bg-green-500/20  text-green-400',
  Failed:  'bg-red-500/20    text-red-400',
};

// source label map
const sourceLabel = (src) => {
  if (!src) return { label: 'Online', cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
  if (src === 'WalkIn') return { label: 'Admin',  cls: 'bg-teal-500/20   text-teal-400   border-teal-500/30'   };
  if (src === 'OTA')    return { label: 'OTA',    cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
  return { label: src, cls: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
};

const fmtDate  = (d) => new Date(d.endsWith?.('Z') ? d : d + 'Z');
const fmtShort = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const fmtMoney = (n) => `৳${Number(n).toLocaleString('en-BD')}`;
const monthKey = (iso) => iso.slice(0, 7);
const isOnOrBeforeToday = (d) => fmtDate(d) <= new Date();

// ── main component ────────────────────────────────────────────────────────────

export default function Calendar() {
  const navigate = useNavigate();
  const todayIso = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [loading, setLoading]           = useState(false);
  const [dayData, setDayData]           = useState(null);
  const [roomFilter, setRoomFilter]     = useState('All');

  // Booking detail dialog
  const [detailId,      setDetailId]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment dialog
  const [payOpen,    setPayOpen]    = useState(false);
  const [payMode,    setPayMode]    = useState('amount');  // 'amount' | 'percent'
  const [payInput,   setPayInput]   = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // ── data loading ────────────────────────────────────────────────────────────

  const load = useCallback(async (iso) => {
    if (!iso) return;
    setLoading(true);
    try {
      const [yr, mo, dy] = iso.split('-').map(Number);
      const [bookingData, calData] = await Promise.all([
        getAdminBookings({ dateFrom: iso, dateTo: iso, pageSize: 300 }),
        getRoomCalendar(monthKey(iso)),
      ]);
      const d = new Date(yr, mo - 1, dy);
      const dayBookings = (bookingData.items || []).filter(b => {
        const ci = fmtDate(b.checkInDate), co = fmtDate(b.checkOutDate);
        return d >= new Date(ci.getFullYear(), ci.getMonth(), ci.getDate()) &&
               d <  new Date(co.getFullYear(), co.getMonth(), co.getDate());
      });
      const rooms = (calData?.rooms ?? [])
        .map(r => ({
          roomId:       r.roomId,
          roomTypeId:   r.roomTypeId,
          roomNumber:   r.roomNumber,
          roomTypeName: r.roomTypeName,
          status:       r.days.find(ds => ds.day === dy)?.status ?? 'Free',
        }))
        .sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }));
      setDayData({ iso, bookings: dayBookings, rooms });
    } catch (e) {
      toast.error(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(todayIso); }, []); // eslint-disable-line

  const handleDateChange = (e) => {
    const iso = e.target.value;
    setSelectedDate(iso);
    setDayData(null);
    if (iso) load(iso);
  };

  // ── booking detail ──────────────────────────────────────────────────────────

  const openDetail = async (id) => {
    setDetailId(id); setDetail(null); setDetailLoading(true);
    try { setDetail(await getAdminBookingDetail(id)); }
    catch { toast.error('Failed to load booking'); }
    finally { setDetailLoading(false); }
  };

  const closeDetail = () => { setDetailId(null); setDetail(null); };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await checkIn(detailId);
      toast.success('Guest checked in');
      setDetail(await getAdminBookingDetail(detailId));
      load(selectedDate);
    } catch (e) { toast.error(e.message); }
    finally { setActionLoading(false); }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      await checkOut(detailId);
      toast.success('Guest checked out');
      setDetail(await getAdminBookingDetail(detailId));
      load(selectedDate);
    } catch (e) { toast.error(e.message); }
    finally { setActionLoading(false); }
  };

  const openPay = () => { setPayMode('amount'); setPayInput(''); setPayOpen(true); };

  const handleConfirmPayment = async () => {
    const val = parseFloat(payInput);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid value'); return; }
    if (payMode === 'percent' && val > 100) { toast.error('Discount cannot exceed 100%'); return; }
    setPayLoading(true);
    try {
      const amt  = payMode === 'percent' ? detail.grandTotal * (1 - val / 100) : val;
      const pct  = payMode === 'percent' ? val : null;
      const updated = await confirmPayment(detailId, amt, pct);
      setDetail(updated);
      setPayOpen(false);
      toast.success('Payment confirmed');
      load(selectedDate);
    } catch (e) { toast.error(e.message); }
    finally { setPayLoading(false); }
  };

  // ── derived values ──────────────────────────────────────────────────────────

  const stats = (() => {
    if (!dayData) return null;
    const c = { total: dayData.bookings.length, Hold: 0, Confirmed: 0, CheckedIn: 0, CheckedOut: 0, Cancelled: 0, NoShow: 0 };
    dayData.bookings.forEach(b => { if (c[b.status] !== undefined) c[b.status]++; });
    c.available = dayData.rooms.filter(r => r.status === 'Free').length;
    return c;
  })();

  const freeCount    = dayData?.rooms.filter(r => r.status === 'Free').length    ?? 0;
  const bookedCount  = dayData?.rooms.filter(r => r.status === 'Booked').length  ?? 0;
  const blockedCount = dayData?.rooms.filter(r => r.status === 'Blocked').length ?? 0;

  const ROOM_FILTERS = [
    { key: 'All',       count: dayData?.rooms.length ?? 0, active: 'bg-slate-600 text-white border-transparent',        idle: 'bg-slate-800 text-slate-400 border-slate-700'        },
    { key: 'Available', count: freeCount,                  active: 'bg-green-600 text-white border-transparent',        idle: 'bg-green-500/10 text-green-400 border-green-500/30'  },
    { key: 'Booked',    count: bookedCount,                active: 'bg-blue-600  text-white border-transparent',        idle: 'bg-blue-500/10  text-blue-400  border-blue-500/30'   },
    { key: 'Blocked',   count: blockedCount,               active: 'bg-red-600   text-white border-transparent',        idle: 'bg-red-500/10   text-red-400   border-red-500/30'    },
  ];

  const filteredRooms = !dayData ? [] : roomFilter === 'All'
    ? dayData.rooms
    : dayData.rooms.filter(r =>
        roomFilter === 'Available' ? r.status === 'Free' : r.status === roomFilter
      );

  // payment live calc
  const payInputNum = parseFloat(payInput) || 0;
  const payCalcAmt = payMode === 'percent'
    ? (detail ? detail.grandTotal * (1 - payInputNum / 100) : 0)
    : payInputNum;
  const payCalcPct = payMode === 'amount' && detail && detail.grandTotal > 0
    ? ((detail.grandTotal - payInputNum) / detail.grandTotal * 100)
    : payInputNum;
  const discountAmt = detail ? detail.grandTotal - payCalcAmt : 0;

  const displayDate = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB',
        { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Booking Calendar</h1>
          <p className="text-slate-400 text-sm mt-0.5">Select a date to view room availability and bookings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={() => load(selectedDate)}
            disabled={!selectedDate || loading} className="text-slate-400 hover:text-white">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => navigate('/admin-booking')} className="bg-blue-600 hover:bg-blue-700 text-sm">
            + Walk-in Booking
          </Button>
        </div>
      </div>

      {/* Date picker */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-blue-400" />
              <label className="text-sm text-slate-300 font-medium">Select Date</label>
            </div>
            <input
              type="date" value={selectedDate} onChange={handleDateChange}
              className="bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            />
            {displayDate && <span className="text-slate-300 text-sm font-medium">{displayDate}</span>}
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}

      {!loading && dayData && (
        <div className="space-y-4">

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {STATS.map(({ key, label, bg, text }) => (
              <div key={key} className={`${bg} border border-white/5 rounded-xl p-3 text-center`}>
                <div className={`text-2xl font-bold ${text}`}>{stats[key] ?? 0}</div>
                <div className="text-xs text-slate-400 mt-0.5 leading-tight">{label}</div>
              </div>
            ))}
          </div>

          {/* Room grid */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <BedDouble className="h-4 w-4 text-blue-400" />
                <h3 className="text-sm font-semibold text-white">Rooms</h3>
                <span className="text-xs text-slate-500 ml-1">— click Available room to book</span>
              </div>

              {/* Filter tabs */}
              <div className="flex gap-2 flex-wrap mb-4">
                {ROOM_FILTERS.map(f => (
                  <button key={f.key} onClick={() => setRoomFilter(f.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all
                      ${roomFilter === f.key ? f.active : f.idle}`}>
                    {f.key}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${roomFilter === f.key ? 'bg-white/20' : 'bg-slate-700 text-slate-300'}`}>
                      {f.count}
                    </span>
                  </button>
                ))}
              </div>

              {dayData.rooms.length === 0 ? (
                <p className="text-slate-500 text-sm">No rooms found. Add physical rooms in the Rooms section first.</p>
              ) : filteredRooms.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">No {roomFilter.toLowerCase()} rooms.</p>
              ) : (
                <div className="overflow-y-auto max-h-72 pr-1">
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 xl:grid-cols-12 gap-2">
                    {filteredRooms.map(room => {
                      const s = ROOM_STYLE[room.status] ?? ROOM_STYLE.Free;
                      const isAvailable = room.status === 'Free';
                      return (
                        <div
                          key={room.roomId}
                          title={isAvailable ? `Click to book ${room.roomNumber}` : `${room.roomTypeName} — ${room.status}`}
                          onClick={() => {
                            if (isAvailable)
                              navigate(`/admin-booking?checkIn=${selectedDate}&roomTypeId=${room.roomTypeId}`);
                          }}
                          className={`border rounded-xl p-3 text-center flex flex-col items-center gap-1 transition-all ${s.card}
                            ${isAvailable ? 'hover:scale-105 active:scale-95' : ''}`}
                        >
                          <div className="text-lg font-bold text-white leading-none">{room.roomNumber}</div>
                          <div className="text-[10px] text-slate-400 truncate w-full text-center leading-none">{room.roomTypeName}</div>
                          <div className={`flex items-center gap-1 text-[10px] font-semibold ${s.label}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
                            {room.status === 'Free' ? 'Available' : room.status}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bookings table */}
          {dayData.bookings.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Bookings ({dayData.bookings.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        {['PNR','Guest','Phone','Room Type','Check-In','Check-Out','Source','Status','Payment',''].map(h => (
                          <th key={h} className="text-left text-xs text-slate-400 pb-2 pr-3 font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/40">
                      {dayData.bookings.map(b => {
                        const src = sourceLabel(b.source);
                        return (
                          <tr key={b.bookingId} className="hover:bg-slate-700/30 transition-colors">
                            <td className="py-2.5 pr-3 font-mono text-blue-400 text-xs">{b.pnr}</td>
                            <td className="py-2.5 pr-3 text-white font-medium whitespace-nowrap">{b.guestName}</td>
                            <td className="py-2.5 pr-3 text-slate-400 text-xs">{b.guestPhone || '—'}</td>
                            <td className="py-2.5 pr-3 text-slate-300 whitespace-nowrap">{b.roomTypeName}</td>
                            <td className="py-2.5 pr-3 text-slate-400 text-xs whitespace-nowrap">{fmtShort(b.checkInDate)}</td>
                            <td className="py-2.5 pr-3 text-slate-400 text-xs whitespace-nowrap">{fmtShort(b.checkOutDate)}</td>
                            <td className="py-2.5 pr-3">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${src.cls}`}>{src.label}</span>
                            </td>
                            <td className="py-2.5 pr-3">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${STATUS_STYLE[b.status] || ''}`}>{b.status}</span>
                            </td>
                            <td className="py-2.5 pr-3">
                              <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${PAY_STYLE[b.paymentStatus] || ''}`}>{b.paymentStatus}</span>
                            </td>
                            <td className="py-2.5">
                              <Button variant="ghost" size="icon" onClick={() => openDetail(b.bookingId)}
                                className="h-7 w-7 text-slate-400 hover:text-white">
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {dayData.bookings.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-4">No bookings on this date.</p>
          )}
        </div>
      )}

      {!loading && !dayData && (
        <div className="flex flex-col items-center justify-center py-16 text-slate-500 space-y-2">
          <CalendarDays className="h-12 w-12 opacity-20" />
          <p className="text-sm">Pick a date above to see room availability and bookings</p>
        </div>
      )}

      {/* ── Booking Detail Dialog ── */}
      <Dialog open={detailId !== null} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-400" /> Booking Detail
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Status + source + actions */}
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex flex-wrap gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${STATUS_STYLE[detail.status] || ''}`}>{detail.status}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${PAY_STYLE[detail.paymentStatus] || ''}`}>{detail.paymentStatus}</span>
                  {(() => { const s = sourceLabel(detail.source); return (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${s.cls}`}>{s.label}</span>
                  ); })()}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {detail.paymentStatus === 'Pending' && !['Cancelled','CheckedOut','NoShow'].includes(detail.status) && (
                    <Button size="sm" onClick={openPay} disabled={actionLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs">
                      <Banknote className="h-3.5 w-3.5 mr-1.5" /> Confirm Payment
                    </Button>
                  )}
                  {['Confirmed','Hold'].includes(detail.status) && detail.paymentStatus !== 'Pending' && isOnOrBeforeToday(detail.checkInDate) && (
                    <Button size="sm" onClick={handleCheckIn} disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs">
                      <LogIn className="h-3.5 w-3.5 mr-1.5" /> Check In
                    </Button>
                  )}
                  {detail.status === 'CheckedIn' && (
                    <Button size="sm" onClick={handleCheckOut} disabled={actionLoading}
                      className="bg-slate-600 hover:bg-slate-500 text-white h-8 text-xs">
                      <LogOut className="h-3.5 w-3.5 mr-1.5" /> Check Out
                    </Button>
                  )}
                </div>
              </div>

              <Card className="bg-slate-700/50 border-slate-600">
                <CardContent className="px-4 py-3 space-y-0">
                  {[
                    ['PNR',        <span className="font-mono text-blue-400">{detail.pnr}</span>],
                    ['Room Type',  detail.roomTypeName],
                    ['Rooms',      detail.numberOfRooms],
                    ['Guests',     `${detail.adults} Adult${detail.adults !== 1 ? 's' : ''}${detail.children > 0 ? `, ${detail.children} Children` : ''}`],
                    ['Check-In',   fmtShort(detail.checkInDate)],
                    ['Check-Out',  fmtShort(detail.checkOutDate)],
                    ['Nights',     detail.nights],
                    ['Grand Total', <span className="text-green-400 font-bold">{fmtMoney(detail.grandTotal)}</span>],
                    ['Paid',        <span className="text-blue-400">{fmtMoney(detail.paidAmount)}</span>],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between py-1.5 border-b border-slate-700/50 last:border-0">
                      <span className="text-slate-400 text-sm">{label}</span>
                      <span className="text-white text-sm font-medium text-right">{value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {detail.leadPassenger && (
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="px-4 py-3 space-y-0">
                    {[
                      [<span className="flex items-center gap-1"><User className="h-3 w-3"/>Name</span>, `${detail.leadPassenger.firstName} ${detail.leadPassenger.lastName}`],
                      detail.leadPassenger.phone && [<span className="flex items-center gap-1"><Phone className="h-3 w-3"/>Phone</span>, detail.leadPassenger.phone],
                      detail.leadPassenger.email && [<span className="flex items-center gap-1"><Mail className="h-3 w-3"/>Email</span>, detail.leadPassenger.email],
                    ].filter(Boolean).map(([label, value], i) => (
                      <div key={i} className="flex justify-between py-1.5 border-b border-slate-700/50 last:border-0">
                        <span className="text-slate-400 text-sm">{label}</span>
                        <span className="text-white text-sm font-medium">{value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ── Discount Payment Dialog ── */}
      {detail && (
        <Dialog open={payOpen} onOpenChange={(open) => { setPayOpen(open); }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-amber-400" /> Confirm Payment
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Original total */}
              <div className="bg-slate-700/50 rounded-lg p-3 flex justify-between text-sm">
                <span className="text-slate-400">Original Total</span>
                <span className="text-white font-bold">{fmtMoney(detail.grandTotal)}</span>
              </div>

              {/* Mode toggle */}
              <div className="flex gap-2">
                <button onClick={() => { setPayMode('amount'); setPayInput(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-all
                    ${payMode === 'amount' ? 'bg-blue-600 border-transparent text-white' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'}`}>
                  <DollarSign className="h-3.5 w-3.5" /> Enter Amount
                </button>
                <button onClick={() => { setPayMode('percent'); setPayInput(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-all
                    ${payMode === 'percent' ? 'bg-blue-600 border-transparent text-white' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'}`}>
                  <Percent className="h-3.5 w-3.5" /> Discount %
                </button>
              </div>

              {/* Input */}
              <div className="space-y-1">
                <label className="text-xs text-slate-400">
                  {payMode === 'amount' ? 'Amount to Pay (৳)' : 'Discount Percentage (%)'}
                </label>
                <div className="relative">
                  <input
                    type="number" min="0" step={payMode === 'percent' ? '0.01' : '1'}
                    max={payMode === 'percent' ? '100' : undefined}
                    value={payInput}
                    onChange={e => setPayInput(e.target.value)}
                    placeholder={payMode === 'amount' ? 'e.g. 2000' : 'e.g. 50'}
                    className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-mono
                               focus:outline-none focus:ring-2 focus:ring-amber-500 pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">
                    {payMode === 'percent' ? '%' : '৳'}
                  </span>
                </div>
              </div>

              {/* Live breakdown */}
              {payInput && payInputNum > 0 && (
                <div className="bg-slate-700/40 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5"/>Discount</span>
                    <span className="text-red-400 font-medium">
                      — {fmtMoney(discountAmt)}
                      {payMode === 'amount' && detail.grandTotal > 0 && (
                        <span className="text-slate-500 ml-1.5">({payCalcPct.toFixed(1)}%)</span>
                      )}
                      {payMode === 'percent' && (
                        <span className="text-slate-500 ml-1.5">({payInputNum}%)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-600/50 pt-2">
                    <span className="text-slate-300 font-medium">Amount to Pay</span>
                    <span className="text-green-400 font-bold text-base">{fmtMoney(payCalcAmt)}</span>
                  </div>
                </div>
              )}

              <Button onClick={handleConfirmPayment}
                disabled={payLoading || !payInput || payInputNum <= 0}
                className="w-full bg-amber-600 hover:bg-amber-700">
                {payLoading ? 'Processing…' : `Confirm — Pay ${payInput ? fmtMoney(payCalcAmt) : '…'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
